import type { AudioCodec, VideoCodec } from "mediabunny";

// WhatsApp caps video at 16 MB, but in practice sends fail well before that, so
// we guarantee every converted video lands at or under this conservative target.
export const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const VIDEO_TARGET_BYTES = 13 * 1024 * 1024;

// First-pass encoder budget — aim under the target so VBR overshoot still fits.
// If a pass overshoots we re-encode with a tighter budget (see the loop below).
const ENCODE_BUDGET_BYTES = Math.round(VIDEO_TARGET_BYTES * 0.85);
// Longest edge to scale down to when compressing (keeps a low bitrate watchable).
const RESOLUTION_CAP = 1280;
const AUDIO_BITRATE = 128_000;
const MAX_ENCODE_ATTEMPTS = 2;

// File extensions that indicate a video even when the browser reports no (or a
// bogus) MIME type — common for `.mov`/`.mkv` dragged in from disk.
export const VIDEO_EXTENSIONS = [
  "mp4",
  "mov",
  "m4v",
  "avi",
  "mkv",
  "webm",
  "3gp",
  "3gpp",
  "mpeg",
  "mpg",
  "ogv",
  "wmv",
  "flv",
  "ts",
  "mts",
  "m2ts",
];

// Preference order — H.264/AAC first for the widest WhatsApp/device compatibility.
const VIDEO_CODEC_PREFERENCE: VideoCodec[] = ["avc", "hevc", "av1", "vp9"];
const AUDIO_CODEC_PREFERENCE: AudioCodec[] = ["aac", "opus"];

export type VideoConvertResult =
  | { ok: true; file: File }
  // "unsupported": the browser can't decode/encode this video (no WebCodecs, or
  //   no encodable MP4 codec, or the source codec can't be decoded).
  // "failed": conversion started but errored or produced nothing.
  // "toobig": converted fine but couldn't be shrunk under the size target.
  | { ok: false; reason: "unsupported" | "failed" | "toobig" };

const stripExtension = (name: string) => name.replace(/\.[^./\\]+$/, "");

export const getExtension = (name: string) =>
  name.includes(".") ? (name.split(".").pop() ?? "").toLowerCase() : "";

export const isVideoFile = (file: File) => {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("video/")) return true;
  // A concrete, non-video MIME type is authoritative — a document that merely
  // shares an extension with a video container (a `.ts` TypeScript file,
  // `.mpg`/`.mts` used by other tooling…) must send as a document, not get
  // forced through video conversion and rejected. Only fall back to extension
  // matching when the browser gives us nothing usable — the `.mov`/`.mkv`
  // dragged from disk that report no (or a generic) MIME type.
  if (mime && mime !== "application/octet-stream") return false;
  return VIDEO_EXTENSIONS.includes(getExtension(file.name));
};

export const isMp4File = (file: File) =>
  file.type.toLowerCase() === "video/mp4" || getExtension(file.name) === "mp4";

// Derives a video bitrate that should land the whole file around `targetBytes`,
// leaving room for the audio track. Falls back to a fixed rate if duration is
// unknown.
const bitrateForTarget = (
  targetBytes: number,
  durationSeconds: number,
  hasAudio: boolean,
) => {
  if (durationSeconds <= 0) return 2_000_000;
  const totalBudget = (targetBytes * 8) / durationSeconds;
  return Math.max(
    150_000,
    Math.round(totalBudget - (hasAudio ? AUDIO_BITRATE : 0)),
  );
};

// Re-encodes a video to MP4 with Mediabunny + WebCodecs, guaranteeing the result
// is at or under VIDEO_TARGET_BYTES. Large sources are compressed (bitrate budget
// from duration, plus a resolution cap); if a pass overshoots the target it's
// re-encoded with a tighter budget. Returns a discriminated result rather than
// throwing so the caller can surface each failure mode.
export async function convertVideoToMp4(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<VideoConvertResult> {
  // WebCodecs is the engine here — bail before importing the library if it's
  // missing (e.g. older Firefox), so the caller can fall back gracefully.
  if (typeof window === "undefined" || !("VideoEncoder" in window)) {
    return { ok: false, reason: "unsupported" };
  }

  try {
    const {
      ALL_FORMATS,
      BlobSource,
      BufferTarget,
      Conversion,
      Input,
      Mp4OutputFormat,
      Output,
      getFirstEncodableAudioCodec,
      getFirstEncodableVideoCodec,
    } = await import("mediabunny");

    const outputFormat = new Mp4OutputFormat();
    const supportedCodecs = outputFormat.getSupportedCodecs() as string[];

    // Read metadata (duration + display size) once from a throwaway Input; each
    // encode attempt below gets its own fresh Input.
    const metaInput = new Input({
      formats: ALL_FORMATS,
      source: new BlobSource(file),
    });

    let duration = 0;
    try {
      duration = await metaInput.computeDuration();
    } catch {
      duration = 0;
    }

    const needsShrink = file.size > VIDEO_TARGET_BYTES;

    // Cap resolution only when we're actually shrinking a large video.
    let width: number | undefined;
    if (needsShrink) {
      try {
        const videoTrack = await metaInput.getPrimaryVideoTrack();
        if (videoTrack) {
          const w = await videoTrack.getDisplayWidth();
          const h = await videoTrack.getDisplayHeight();
          const longSide = Math.max(w, h);
          if (longSide > RESOLUTION_CAP) {
            // Height is derived from the aspect ratio by Mediabunny. Keep width
            // even to satisfy H.264's chroma subsampling.
            width = Math.round((w * (RESOLUTION_CAP / longSide)) / 2) * 2;
          }
        }
      } catch {
        // Metadata read failed — proceed without a resolution cap.
      }
    }

    const audioCodec = await getFirstEncodableAudioCodec(
      AUDIO_CODEC_PREFERENCE.filter((c) => supportedCodecs.includes(c)),
    );

    // Start near the source size for small files; compress large ones to budget.
    let targetBytes = needsShrink
      ? ENCODE_BUDGET_BYTES
      : Math.min(file.size, VIDEO_TARGET_BYTES);

    const videoCodec = await getFirstEncodableVideoCodec(
      VIDEO_CODEC_PREFERENCE.filter((c) => supportedCodecs.includes(c)),
      {
        width,
        bitrate: bitrateForTarget(targetBytes, duration, !!audioCodec),
      },
    );
    if (!videoCodec) return { ok: false, reason: "unsupported" };

    const baseName = stripExtension(file.name) || "video";
    let smallest: File | null = null;

    for (let attempt = 0; attempt < MAX_ENCODE_ATTEMPTS; attempt++) {
      const videoBitrate = bitrateForTarget(
        targetBytes,
        duration,
        !!audioCodec,
      );
      const output = new Output({
        format: outputFormat,
        target: new BufferTarget(),
      });
      const conversion = await Conversion.init({
        input: new Input({
          formats: ALL_FORMATS,
          source: new BlobSource(file),
        }),
        output,
        video: {
          codec: videoCodec,
          bitrate: videoBitrate,
          ...(width ? { width } : {}),
        },
        audio: audioCodec
          ? { codec: audioCodec, bitrate: AUDIO_BITRATE }
          : { discard: true },
      });
      if (!conversion.isValid) return { ok: false, reason: "failed" };
      if (onProgress)
        conversion.onProgress = (progress) => onProgress(progress);

      await conversion.execute();

      const buffer = output.target.buffer;
      if (!buffer) return { ok: false, reason: "failed" };

      const result = new File([buffer], `${baseName}.mp4`, {
        type: "video/mp4",
      });
      if (!smallest || result.size < smallest.size) smallest = result;
      if (result.size <= VIDEO_TARGET_BYTES) return { ok: true, file: result };

      // Overshot the target — tighten the budget proportionally (with margin)
      // and try once more.
      targetBytes = Math.round(
        targetBytes * (VIDEO_TARGET_BYTES / result.size) * 0.85,
      );
    }

    return smallest && smallest.size <= VIDEO_TARGET_BYTES
      ? { ok: true, file: smallest }
      : { ok: false, reason: "toobig" };
  } catch {
    return { ok: false, reason: "failed" };
  }
}
